"use client";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

// ============================================================================
// Permission map — per-role boolean grid
// ============================================================================

type RoleKey = 1 | 2 | 3 | 4;
type PermKey =
  | "p_leads_own"
  | "p_leads_edit"
  | "p_leads_team"
  | "p_calls_make"
  | "p_calls_recordings"
  | "p_calls_live"
  | "p_emails_send"
  | "p_emails_templates"
  | "p_meetings_book"
  | "p_meetings_pool"
  | "p_team_invite"
  | "p_team_roles";

type CategoryKey = "leads" | "calls" | "emails" | "meetings" | "team";

const CATEGORIES: { key: CategoryKey; perms: PermKey[] }[] = [
  { key: "leads", perms: ["p_leads_own", "p_leads_edit", "p_leads_team"] },
  { key: "calls", perms: ["p_calls_make", "p_calls_recordings", "p_calls_live"] },
  { key: "emails", perms: ["p_emails_send", "p_emails_templates"] },
  { key: "meetings", perms: ["p_meetings_book", "p_meetings_pool"] },
  { key: "team", perms: ["p_team_invite", "p_team_roles"] },
];

const PERMISSION_MAP: Record<RoleKey, Record<PermKey, boolean>> = {
  1: {
    // Setter — Cold calling operator
    p_leads_own: true,
    p_leads_edit: true,
    p_leads_team: false,
    p_calls_make: true,
    p_calls_recordings: false,
    p_calls_live: false,
    p_emails_send: true,
    p_emails_templates: false,
    p_meetings_book: true,
    p_meetings_pool: false,
    p_team_invite: false,
    p_team_roles: false,
  },
  2: {
    // Backoffice — Operations & docs (no calling)
    p_leads_own: true,
    p_leads_edit: true,
    p_leads_team: true,
    p_calls_make: false,
    p_calls_recordings: true,
    p_calls_live: false,
    p_emails_send: true,
    p_emails_templates: true,
    p_meetings_book: true,
    p_meetings_pool: false,
    p_team_invite: false,
    p_team_roles: false,
  },
  3: {
    // Closer — Senior, broad rights
    p_leads_own: true,
    p_leads_edit: true,
    p_leads_team: true,
    p_calls_make: true,
    p_calls_recordings: true,
    p_calls_live: true,
    p_emails_send: true,
    p_emails_templates: false,
    p_meetings_book: true,
    p_meetings_pool: true,
    p_team_invite: false,
    p_team_roles: false,
  },
  4: {
    // Read-only — QC / reporting
    p_leads_own: true,
    p_leads_edit: false,
    p_leads_team: true,
    p_calls_make: false,
    p_calls_recordings: true,
    p_calls_live: false,
    p_emails_send: false,
    p_emails_templates: false,
    p_meetings_book: false,
    p_meetings_pool: false,
    p_team_invite: false,
    p_team_roles: false,
  },
};

const TOTAL_PERMS = (Object.keys(PERMISSION_MAP[1]) as PermKey[]).length;
const ROLE_KEYS: RoleKey[] = [1, 2, 3, 4];
const ROTATION_MS = 5000;

// ============================================================================
// Root Section
// ============================================================================

export const RoleManagementSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <RoleScene />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("roles");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <ShieldIcon className="h-3.5 w-3.5" />
        Role Management
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Role Scene
// ============================================================================

const RoleScene = () => {
  const t = useTranslations("roles");
  const [active, setActive] = React.useState<RoleKey>(1);

  React.useEffect(() => {
    const id = setInterval(() => {
      setActive((prev) => {
        const i = ROLE_KEYS.indexOf(prev);
        return ROLE_KEYS[(i + 1) % ROLE_KEYS.length];
      });
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[620px]">
        <RolesRail active={active} onSelect={setActive} />
        <PermissionsPanel active={active} />
      </div>
      <div className="border-t border-neutral-800 bg-neutral-900/40 px-5 py-3 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="text-[10px] text-neutral-500">{t("footer_meta")}</span>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Roles rail (left)
// ----------------------------------------------------------------------------

const RolesRail = ({
  active,
  onSelect,
}: {
  active: RoleKey;
  onSelect: (k: RoleKey) => void;
}) => {
  const t = useTranslations("roles");
  return (
    <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-neutral-900/40 p-5 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <ShieldIcon className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {t("roles_panel_label")}
          </span>
        </div>
        <span className="text-[10px] text-neutral-500 hover:text-neutral-300 cursor-pointer">
          {t("new_role_button")}
        </span>
      </div>

      <ul className="space-y-2 flex-1">
        {ROLE_KEYS.map((key) => {
          const isActive = key === active;
          const enabled = countEnabled(key);
          return (
            <li key={key}>
              <button
                type="button"
                onClick={() => onSelect(key)}
                className={cn(
                  "w-full text-left rounded-xl border p-3 transition-all",
                  isActive
                    ? "border-red-500/50 bg-neutral-900"
                    : "border-neutral-800 bg-neutral-900/40 hover:border-neutral-700 hover:bg-neutral-900/60"
                )}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      "flex h-3 w-3 flex-shrink-0 items-center justify-center rounded-full",
                      isActive
                        ? "bg-red-500"
                        : "bg-neutral-700"
                    )}
                  >
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={cn(
                          "text-sm font-semibold truncate",
                          isActive ? "text-white" : "text-neutral-300"
                        )}
                      >
                        {t(`role_${key}_name`)}
                      </span>
                      <span
                        className={cn(
                          "text-[10px] font-mono tabular-nums whitespace-nowrap flex-shrink-0",
                          isActive ? "text-red-300" : "text-neutral-500"
                        )}
                      >
                        {enabled}/{TOTAL_PERMS}
                      </span>
                    </div>
                    <div className="text-[11px] text-neutral-500 truncate mt-0.5">
                      {t(`role_${key}_subtitle`)}
                    </div>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 rounded-xl border border-neutral-800 bg-neutral-900/60 p-3 flex items-start gap-2">
        <LockIcon className="h-4 w-4 flex-shrink-0 text-neutral-500 mt-0.5" />
        <span className="text-[11px] text-neutral-500 leading-relaxed">
          {t("hierarchy_note")}
        </span>
      </div>
    </div>
  );
};

const countEnabled = (role: RoleKey): number => {
  const map = PERMISSION_MAP[role];
  return Object.values(map).filter(Boolean).length;
};

// ----------------------------------------------------------------------------
// Permissions panel (right)
// ----------------------------------------------------------------------------

const PermissionsPanel = ({ active }: { active: RoleKey }) => {
  const t = useTranslations("roles");
  const enabled = countEnabled(active);
  return (
    <div className="lg:col-span-8 relative overflow-hidden p-5 bg-neutral-950 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <BoltIcon className="h-3.5 w-3.5 text-red-500" />
            <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
              {t("permissions_for")} {t(`role_${active}_name`)}
            </span>
          </div>
          <div className="text-[10px] text-neutral-500 mt-0.5">{t("perms_meta")}</div>
        </div>
        <span className="text-[11px] font-mono tabular-nums text-neutral-400 bg-neutral-900 border border-neutral-800 rounded px-2 py-0.5 whitespace-nowrap">
          {enabled} {t("of_total")} {TOTAL_PERMS} {t("active_label")}
        </span>
      </div>

      {/* Categories */}
      <div className="space-y-4 flex-1">
        {CATEGORIES.map((cat) => (
          <CategoryBlock key={cat.key} category={cat} role={active} />
        ))}
      </div>
    </div>
  );
};

const CategoryBlock = ({
  category,
  role,
}: {
  category: { key: CategoryKey; perms: PermKey[] };
  role: RoleKey;
}) => {
  const t = useTranslations("roles");
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/40 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-semibold tracking-wider text-neutral-400 uppercase">
          {t(`cat_${category.key}`)}
        </span>
        <span className="h-px flex-1 bg-neutral-800" />
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
        {category.perms.map((perm) => (
          <PermissionRow key={perm} perm={perm} role={role} />
        ))}
      </ul>
    </div>
  );
};

const PermissionRow = ({ perm, role }: { perm: PermKey; role: RoleKey }) => {
  const t = useTranslations("roles");
  const enabled = PERMISSION_MAP[role][perm];
  return (
    <motion.li
      key={`${perm}-${role}`}
      initial={{ opacity: 0.5, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.25 }}
      className="flex items-center gap-2 py-1"
    >
      <span
        className={cn(
          "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors",
          enabled
            ? "border-red-500/50 bg-red-500/15 text-red-300"
            : "border-neutral-800 bg-neutral-900 text-neutral-700"
        )}
      >
        {enabled ? <CheckIcon className="h-3 w-3" /> : <DashIcon className="h-2.5 w-2.5" />}
      </span>
      <span
        className={cn(
          "text-sm leading-tight",
          enabled ? "text-neutral-200" : "text-neutral-600"
        )}
      >
        {t(perm)}
      </span>
    </motion.li>
  );
};

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconTemplate /> },
  { key: "t2", icon: <IconGranular /> },
  { key: "t3", icon: <IconHierarchy /> },
  { key: "t4", icon: <IconScope /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("roles");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4 12l5 5L20 6" />
    </svg>
  );
}

function DashIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M6 12h12" />
    </svg>
  );
}

function IconTemplate() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function IconGranular() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M4 6h16M4 10h10M4 14h16M4 18h7" />
      <circle cx="20" cy="10" r="1.5" fill="currentColor" />
      <circle cx="14" cy="18" r="1.5" fill="currentColor" />
    </svg>
  );
}

function IconHierarchy() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <rect x="3" y="14" width="6" height="4" rx="1" />
      <rect x="9" y="14" width="6" height="4" rx="1" />
      <rect x="15" y="14" width="6" height="4" rx="1" />
      <path d="M12 7v3M6 14v-2.5h12V14" />
    </svg>
  );
}

function IconScope() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="9" cy="9" r="5" />
      <circle cx="15" cy="15" r="5" />
    </svg>
  );
}
